import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminDealers() {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // null | 'add' | 'edit'
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({});
  const [logoFile,setLogoFile]= useState(null);
  const [saving,  setSaving]  = useState(false);

  const loadAll = () => {
    setLoading(true);
    api.get('/dealers/admin')
      .then(r => setDealers(r.data.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadAll(); }, []);

  const openModal = (dealer = null) => {
    setEditing(dealer);
    setForm(dealer
      ? { name: dealer.name, website: dealer.website || '', isActive: dealer.isActive, sortOrder: dealer.sortOrder }
      : { name: '', website: '', isActive: true, sortOrder: 0 });
    setLogoFile(null);
    setModal(dealer ? 'edit' : 'add');
  };

  const save = async () => {
    if (!form.name) { toast.error('Dealer / company name required'); return; }
    if (!editing && !logoFile) { toast.error('Logo image required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (logoFile) fd.append('logo', logoFile);
      if (editing) {
        const r = await api.put(`/dealers/${editing._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setDealers(ds => ds.map(d => d._id === editing._id ? r.data.data : d));
        toast.success('Dealer updated!');
      } else {
        const r = await api.post('/dealers', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setDealers(ds => [...ds, r.data.data]);
        toast.success('Dealer added!');
      }
      setModal(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this dealer logo?')) return;
    try { await api.delete(`/dealers/${id}`); setDealers(ds => ds.filter(d => d._id !== id)); toast.success('Removed.'); }
    catch { toast.error('Failed to remove.'); }
  };

  const h = e => setForm(f => ({ ...f, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-headline font-black text-3xl uppercase tracking-tight">Dealers</h1>
          <p className="font-body text-sm text-secondary mt-1">
            Manage the company / brand logos shown in the "Our Dealerships" section on the homepage.
          </p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <span className="material-symbols-outlined">add</span>
          Add Dealer Logo
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_,i) => <div key={i} className="h-14 skeleton" />)}</div>
      ) : (
        <div className="bg-white border border-outline-variant/20 overflow-hidden">
          <table className="cf-table">
            <thead><tr><th>Logo</th><th>Company Name</th><th>Website</th><th>Status</th><th>Order</th><th>Actions</th></tr></thead>
            <tbody>
              {dealers.map(dealer => (
                <tr key={dealer._id}>
                  <td>
                    <div className="w-16 h-12 bg-surface-container flex items-center justify-center p-1">
                      <img src={dealer.logo} alt={dealer.name} className="max-w-full max-h-full object-contain" />
                    </div>
                  </td>
                  <td className="font-bold">{dealer.name}</td>
                  <td className="text-secondary text-xs">
                    {dealer.website
                      ? <a href={dealer.website} target="_blank" rel="noreferrer" className="text-primary-container hover:underline">{dealer.website}</a>
                      : '—'}
                  </td>
                  <td><span className={`badge ${dealer.isActive ? 'badge-green' : 'badge-red'}`}>{dealer.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td>{dealer.sortOrder}</td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => openModal(dealer)} className="p-1.5 text-secondary hover:text-primary-container transition-colors">
                        <span className="material-symbols-outlined text-[18px]">edit</span>
                      </button>
                      <button onClick={() => remove(dealer._id)} className="p-1.5 text-secondary hover:text-error transition-colors">
                        <span className="material-symbols-outlined text-[18px]">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {dealers.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-secondary">No dealer logos added yet. Click "Add Dealer Logo" to add the first one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Modal */}
      {(modal === 'add' || modal === 'edit') && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-container">
              <h2 className="font-headline font-black text-xl uppercase">{editing ? 'Edit Dealer' : 'Add Dealer Logo'}</h2>
              <button onClick={() => setModal(null)}><span className="material-symbols-outlined">close</span></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="input-label">Company / Brand Name *</label>
                <input name="name" value={form.name || ''} onChange={h} className="input-field" placeholder="e.g. Tata Steel" />
              </div>
              <div>
                <label className="input-label">Logo Image {!editing && '*'}</label>
                <input type="file" accept="image/*" onChange={e => setLogoFile(e.target.files[0])} className="input-field" />
                <p className="font-body text-xs text-secondary mt-1">PNG with transparent background works best. Max 10MB.</p>
                {editing?.logo && (
                  <div className="mt-2 w-24 h-16 bg-surface-container flex items-center justify-center p-1">
                    <img src={editing.logo} alt="" className="max-w-full max-h-full object-contain" />
                  </div>
                )}
              </div>
              <div>
                <label className="input-label">Website Link (optional)</label>
                <input name="website" value={form.website || ''} onChange={h} className="input-field" placeholder="https://example.com" />
                <p className="font-body text-xs text-secondary mt-1">If set, clicking the logo will open this link in a new tab.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Sort Order</label>
                  <input name="sortOrder" type="number" value={form.sortOrder || 0} onChange={h} className="input-field" />
                </div>
                <div className="flex items-end pb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name="isActive" checked={!!form.isActive} onChange={h} className="w-4 h-4 accent-primary-container" />
                    <span className="font-label text-sm font-bold uppercase tracking-wider">Active</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-surface-container flex justify-end gap-3">
              <button onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary">{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}